import { JsonValue, isJsonObject, stableStringify } from "../json";
import {
  ScalarBase,
  TypeRef,
  TypeSystem,
  findType,
  isAssignable,
  listOf,
  named,
  nonNull,
  scalarBase,
  typeToString,
} from "../types/type-system";

/** A value argument's type, and its value when it's a literal. */
export type ArgInfo =
  | { kind: "value"; type: TypeRef; literal?: { value: JsonValue } }
  | { kind: "lambda" };

export interface TypeCheckContext {
  ts: TypeSystem;
  /** Infers a lambda argument's body type given the element type bound to its parameter. */
  lambdaType(index: number, paramType: TypeRef): TypeRef | string;
}

export interface EvalContext {
  readonly count: number;
  arg(index: number): JsonValue;
  lambda(index: number): (value: JsonValue) => JsonValue;
  /** Run clock: the evaluator never reads the wall clock, so replay is deterministic. */
  now: string;
}

export type FunctionCategory =
  | "logic"
  | "comparison"
  | "text"
  | "list"
  | "datetime"
  | "number"
  | "existence";

export interface FunctionDef {
  name: string;
  label: string;
  category: FunctionCategory;
  typeCheck(ctx: TypeCheckContext, args: ArgInfo[]): TypeRef | string;
  evaluate(ctx: EvalContext): JsonValue;
}

const BOOLEAN = nonNull("Boolean");
const NUMBER = nonNull("Float");
const DATETIME = nonNull("DateTime");

export const TIME_UNITS = [
  "second",
  "minute",
  "hour",
  "day",
  "week",
  "month",
  "year",
] as const;
export type TimeUnit = (typeof TIME_UNITS)[number];

export const DATE_PARTS = [
  "year",
  "month",
  "day",
  "dayOfWeek",
  "hour",
] as const;

function arity(args: ArgInfo[], min: number, max = min): string | undefined {
  if (args.length < min || args.length > max) {
    return max === min
      ? `expects ${min} argument(s)`
      : `expects ${min}-${max} arguments`;
  }
  return undefined;
}

function valueArg(args: ArgInfo[], i: number): TypeRef | string {
  const arg = args[i];
  if (!arg) {
    return `missing argument ${i + 1}`;
  }
  if (arg.kind !== "value") {
    return `argument ${i + 1} must be a value`;
  }
  return arg.type;
}

function baseOf(ts: TypeSystem, type: TypeRef): ReturnType<typeof scalarBase> {
  return scalarBase(ts, type);
}

function isDateBase(base: ReturnType<typeof scalarBase>): boolean {
  return base === "datetime" || base === "date";
}

/** Values that are text at runtime: dates and timestamps are ISO strings. */
function isTextBase(base: ReturnType<typeof scalarBase>): boolean {
  return (
    base === "string" || base === "enum" || base === "json" || isDateBase(base)
  );
}

/** Equality also compares text with dates (both are strings); ordering doesn't. */
function comparable(
  ts: TypeSystem,
  a: TypeRef,
  b: TypeRef,
  ordered: boolean,
): boolean {
  const ba = baseOf(ts, a);
  const bb = baseOf(ts, b);
  if (ba === "json" || bb === "json") {
    return true;
  }
  if (isDateBase(ba) && isDateBase(bb)) {
    return true;
  }
  const textual = (x: typeof ba) => x === "string" || x === "enum";
  if (textual(ba) && textual(bb)) {
    return true;
  }
  if (
    !ordered &&
    ((textual(ba) && isDateBase(bb)) || (isDateBase(ba) && textual(bb)))
  ) {
    return true;
  }
  return ba === bb;
}

const ORDERED_BASES: readonly ReturnType<typeof scalarBase>[] = [
  "number",
  "datetime",
  "date",
  "string",
  "json",
];

/**
 * Milliseconds for a date or datetime string, or null. A datetime without a
 * zone is read as UTC, so every host (and every developer laptop) agrees.
 */
export function parseInstant(value: string): number | null {
  const iso = ISO_DATE.exec(value);
  const normalized =
    iso && iso[1] && !iso[2] ? `${value.replace(" ", "T")}Z` : value;
  const ms = Date.parse(normalized);
  return Number.isNaN(ms) ? null : ms;
}

/** Scalars as compared; objects and lists aren't ordered, so they're null. */
export function toComparable(
  value: JsonValue,
): number | string | boolean | null {
  if (typeof value === "string" && ISO_DATE.test(value)) {
    const ms = parseInstant(value);
    if (ms !== null) {
      return ms;
    }
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  return null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ][\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;

/** Text compares case-insensitively and dates as instants; objects and lists exactly (key order aside). */
export function jsonEquals(a: JsonValue, b: JsonValue): boolean {
  const structuredA = Array.isArray(a) || isJsonObject(a);
  const structuredB = Array.isArray(b) || isJsonObject(b);
  if (structuredA || structuredB) {
    return (
      structuredA && structuredB && stableStringify(a) === stableStringify(b)
    );
  }
  const ca = toComparable(a);
  const cb = toComparable(b);
  if (typeof ca === "string" && typeof cb === "string") {
    return ca.toLowerCase() === cb.toLowerCase();
  }
  return ca === cb;
}

function compare(op: (a: number | string, b: number | string) => boolean) {
  return (ctx: EvalContext): JsonValue => {
    const a = toComparable(ctx.arg(0));
    const b = toComparable(ctx.arg(1));
    if (
      a === null ||
      b === null ||
      typeof a === "boolean" ||
      typeof b === "boolean"
    ) {
      return false;
    }
    if (typeof a !== typeof b) {
      return false;
    }
    return op(a, b);
  };
}

function binaryComparison(
  name: string,
  label: string,
  ordered: boolean,
  evaluate: (ctx: EvalContext) => JsonValue,
): FunctionDef {
  return {
    name,
    label,
    category: "comparison",
    typeCheck(ctx, args) {
      if (args.length === 1) {
        return "needs a value to compare with";
      }
      const err = arity(args, 2);
      if (err) {
        return err;
      }
      const a = valueArg(args, 0);
      const b = valueArg(args, 1);
      if (typeof a === "string") {
        return a;
      }
      if (typeof b === "string") {
        return b;
      }
      if (!comparable(ctx.ts, a, b, ordered)) {
        return `cannot compare ${typeToString(a)} with ${typeToString(b)}`;
      }
      if (ordered && !ORDERED_BASES.includes(baseOf(ctx.ts, a))) {
        return `${typeToString(a)} is not ordered`;
      }
      return BOOLEAN;
    },
    evaluate,
  };
}

function textPredicate(
  name: string,
  label: string,
  test: (a: string, b: string) => boolean,
): FunctionDef {
  return {
    name,
    label,
    category: "text",
    typeCheck(ctx, args) {
      if (args.length === 1) {
        return "needs a value to compare with";
      }
      const err = arity(args, 2);
      if (err) {
        return err;
      }
      for (const i of [0, 1]) {
        const t = valueArg(args, i);
        if (typeof t === "string") {
          return t;
        }
        if (!isTextBase(baseOf(ctx.ts, t))) {
          return `argument ${i + 1} must be text, got ${typeToString(t)}`;
        }
      }
      return BOOLEAN;
    },
    evaluate(ctx) {
      const a = ctx.arg(0);
      const b = ctx.arg(1);
      if (typeof a !== "string" || typeof b !== "string") {
        return false;
      }
      return test(a.toLowerCase(), b.toLowerCase());
    },
  };
}

/** The element type of a list argument; untyped JSON iterates as JSON (a non-array reads as empty). */
function listElement(ctx: TypeCheckContext, type: TypeRef): TypeRef | null {
  if (type.kind === "list") {
    return type.of;
  }
  return baseOf(ctx.ts, type) === "json" ? named("JSON") : null;
}

function isBooleanLike(ctx: TypeCheckContext, type: TypeRef): boolean {
  const base = baseOf(ctx.ts, type);
  return base === "boolean" || base === "json";
}

function listLambda(
  name: string,
  label: string,
  result: (items: JsonValue[], fn: (v: JsonValue) => JsonValue) => JsonValue,
  returns: (
    body: TypeRef,
    element: TypeRef,
    ctx: TypeCheckContext,
  ) => TypeRef | string,
): FunctionDef {
  return {
    name,
    label,
    category: "list",
    typeCheck(ctx, args) {
      const err = arity(args, 2);
      if (err) {
        return err;
      }
      const list = valueArg(args, 0);
      if (typeof list === "string") {
        return list;
      }
      const element = listElement(ctx, list);
      if (!element) {
        return `${name} expects a list, got ${typeToString(list)}`;
      }
      if (args[1]?.kind !== "lambda") {
        return `${name} expects a predicate`;
      }
      const body = ctx.lambdaType(1, element);
      if (typeof body === "string") {
        return body;
      }
      return returns(body, element, ctx);
    },
    evaluate(ctx) {
      const items = ctx.arg(0);
      if (!Array.isArray(items)) {
        return result([], ctx.lambda(1));
      }
      return result(items, ctx.lambda(1));
    },
  };
}

function booleanBody(
  body: TypeRef,
  _element?: TypeRef,
  ctx?: TypeCheckContext,
): TypeRef | string {
  if (body.kind === "named" && body.name === "Boolean") {
    return BOOLEAN;
  }
  return ctx && body.kind === "named" && isBooleanLike(ctx, body)
    ? BOOLEAN
    : "predicate must return a boolean";
}

/** JavaScript String() coercion (null → ""). */
export function jsText(value: JsonValue): string {
  if (value === null) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.map((v) => (v === null ? "" : jsText(v))).join(",");
  }
  if (isJsonObject(value)) {
    return "[object Object]";
  }
  return String(value);
}

function truthy(value: JsonValue): boolean {
  return value === true;
}

export function addTime(
  iso: string,
  amount: number,
  unit: TimeUnit,
): string | null {
  const ms = parseInstant(iso);
  if (ms === null || !Number.isFinite(amount)) {
    return null;
  }
  if (unit !== "month" && unit !== "year") {
    const date = new Date(Math.round(ms + amount * UNIT_MS[unit]));
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  // Calendar units take whole months, and land on the last day of a shorter
  // month (Jan 31 + 1 month is Feb 28) instead of rolling into the next one.
  const months = Math.trunc(amount) * (unit === "year" ? 12 : 1);
  const date = new Date(ms);
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + months);
  const daysInMonth = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0),
  ).getUTCDate();
  date.setUTCDate(Math.min(day, daysInMonth));
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

const TIME_UNIT_NAMES: ReadonlySet<string> = new Set(TIME_UNITS);

export function isTimeUnit(value: JsonValue): value is TimeUnit {
  return typeof value === "string" && TIME_UNIT_NAMES.has(value);
}

const UNIT_MS: Record<TimeUnit, number> = {
  second: 1000,
  minute: 60_000,
  hour: 3_600_000,
  day: 86_400_000,
  week: 604_800_000,
  month: 2_629_746_000,
  year: 31_556_952_000,
};

function isEmptyValue(value: JsonValue): boolean {
  if (value === null || value === "") {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (isJsonObject(value)) {
    return Object.keys(value).length === 0;
  }
  return false;
}

function anyValue(args: ArgInfo[], min: number, max = min): TypeRef | string {
  const err = arity(args, min, max);
  if (err) {
    return err;
  }
  for (let i = 0; i < args.length; i += 1) {
    const t = valueArg(args, i);
    if (typeof t === "string") {
      return t;
    }
  }
  return BOOLEAN;
}

function literalOf(args: ArgInfo[], i: number): { value: JsonValue } | null {
  const arg = args[i];
  return arg?.kind === "value" && arg.literal ? arg.literal : null;
}

/** A unit or part: a literal must be one of `allowed`; other text is checked when it runs (`known` false). */
function choiceArg(
  ctx: TypeCheckContext,
  args: ArgInfo[],
  i: number,
  what: string,
  allowed: readonly string[],
): { known: boolean } | string {
  const t = valueArg(args, i);
  if (typeof t === "string") {
    return t;
  }
  const literal = literalOf(args, i);
  if (literal) {
    return typeof literal.value === "string" && allowed.includes(literal.value)
      ? { known: true }
      : `${what} must be one of ${allowed.join(", ")}`;
  }
  return isTextBase(baseOf(ctx.ts, t))
    ? { known: false }
    : `${what} must be text, got ${typeToString(t)}`;
}

/** The value-argument types, after checking each is a value. */
function valueArgs(args: ArgInfo[]): TypeRef[] | string {
  const types: TypeRef[] = [];
  for (let i = 0; i < args.length; i += 1) {
    const t = valueArg(args, i);
    if (typeof t === "string") {
      return t;
    }
    types.push(t);
  }
  return types;
}

function dateArgs(
  ctx: TypeCheckContext,
  args: ArgInfo[],
  indexes: number[],
): TypeRef[] | string {
  const types: TypeRef[] = [];
  for (const i of indexes) {
    const t = valueArg(args, i);
    if (typeof t === "string") {
      return t;
    }
    if (!isDateBase(baseOf(ctx.ts, t))) {
      return `argument ${i + 1} must be a date, got ${typeToString(t)}`;
    }
    types.push(t);
  }
  return types;
}

/** One-argument text functions: null for anything but text, so nullable unless the input is certainly text. */
function textToText(
  name: string,
  label: string,
  transform: (value: string) => string,
): FunctionDef {
  return {
    name,
    label,
    category: "text",
    typeCheck(ctx, args) {
      const err = arity(args, 1);
      if (err) {
        return err;
      }
      const t = valueArg(args, 0);
      if (typeof t === "string") {
        return t;
      }
      const base = baseOf(ctx.ts, t);
      if (!isTextBase(base)) {
        return `${name} expects text, got ${typeToString(t)}`;
      }
      return { ...nonNull("String"), nullable: t.nullable || base === "json" };
    },
    evaluate(ctx) {
      const value = ctx.arg(0);
      return typeof value === "string" ? transform(value) : null;
    },
  };
}

/** Dates widen to timestamps; text literals take a text-based scalar's type, or an enum's when they're one of its values. */
function fits(
  ts: TypeSystem,
  arg: { type: TypeRef; literal?: { value: JsonValue } },
  target: TypeRef,
): boolean {
  if (isAssignable(ts, arg.type, target)) {
    return true;
  }
  if (arg.type.kind !== "named" || target.kind !== "named") {
    return false;
  }
  const actual = findType(ts, arg.type.name);
  const expected = findType(ts, target.name);
  if (
    actual?.kind === "scalar" &&
    actual.base === "date" &&
    expected?.kind === "scalar" &&
    expected.base === "datetime"
  ) {
    return true;
  }
  const literal = arg.literal?.value;
  if (typeof literal !== "string") {
    return false;
  }
  return expected?.kind === "enum"
    ? expected.values.some((v) => v.value === literal)
    : expected?.kind === "scalar" && expected.base === "string";
}

/** The type every argument fits, preferring the first; non-null when any argument is, since coalesce stops there. */
function unify(
  ts: TypeSystem,
  args: { type: TypeRef; literal?: { value: JsonValue } }[],
): TypeRef | string {
  const types = args.map((a) => a.type);
  const candidates = [
    ...types.filter((t) => !(t.kind === "named" && t.name === "JSON")),
    ...types,
  ];
  const common = candidates.find((c) => args.every((a) => fits(ts, a, c)));
  if (!common) {
    return `arguments must have compatible types, got ${types.map(typeToString).join(", ")}`;
  }
  return { ...common, nullable: types.every((t) => t.nullable) };
}

function numberArgs(
  ctx: TypeCheckContext,
  args: ArgInfo[],
): string | undefined {
  for (let i = 0; i < args.length; i += 1) {
    const t = valueArg(args, i);
    if (typeof t === "string") {
      return t;
    }
    const base = baseOf(ctx.ts, t);
    if (base !== "number" && base !== "json") {
      return `argument ${i + 1} must be a number`;
    }
  }
  return undefined;
}

function arithmetic(
  name: string,
  label: string,
  op: (a: number, b: number) => number | null,
): FunctionDef {
  return {
    name,
    label,
    category: "number",
    typeCheck(ctx, args) {
      const err = arity(args, 2) ?? numberArgs(ctx, args);
      return err ?? { ...NUMBER, nullable: true };
    },
    evaluate(ctx) {
      const a = ctx.arg(0);
      const b = ctx.arg(1);
      return typeof a === "number" && typeof b === "number" ? op(a, b) : null;
    },
  };
}

const definitions: FunctionDef[] = [
  {
    name: "and",
    label: "all of",
    category: "logic",
    typeCheck(ctx, args) {
      if (args.length === 0) {
        return "and expects at least one argument";
      }
      for (let i = 0; i < args.length; i += 1) {
        const t = valueArg(args, i);
        if (typeof t === "string") {
          return t;
        }
        if (!isBooleanLike(ctx, t)) {
          return `argument ${i + 1} must be a boolean`;
        }
      }
      return BOOLEAN;
    },
    evaluate(ctx) {
      for (let i = 0; i < ctx.count; i += 1) {
        if (!truthy(ctx.arg(i))) {
          return false;
        }
      }
      return true;
    },
  },
  {
    name: "or",
    label: "any of",
    category: "logic",
    typeCheck(ctx, args) {
      if (args.length === 0) {
        return "or expects at least one argument";
      }
      for (let i = 0; i < args.length; i += 1) {
        const t = valueArg(args, i);
        if (typeof t === "string") {
          return t;
        }
        if (!isBooleanLike(ctx, t)) {
          return `argument ${i + 1} must be a boolean`;
        }
      }
      return BOOLEAN;
    },
    evaluate(ctx) {
      for (let i = 0; i < ctx.count; i += 1) {
        if (truthy(ctx.arg(i))) {
          return true;
        }
      }
      return false;
    },
  },
  {
    name: "not",
    label: "not",
    category: "logic",
    typeCheck(ctx, args) {
      const err = arity(args, 1);
      if (err) {
        return err;
      }
      const t = valueArg(args, 0);
      if (typeof t === "string") {
        return t;
      }
      return isBooleanLike(ctx, t) ? BOOLEAN : "not expects a boolean";
    },
    evaluate(ctx) {
      return !truthy(ctx.arg(0));
    },
  },
  binaryComparison("eq", "is equal to", false, (ctx) =>
    jsonEquals(ctx.arg(0), ctx.arg(1)),
  ),
  binaryComparison(
    "neq",
    "is not equal to",
    false,
    (ctx) => !jsonEquals(ctx.arg(0), ctx.arg(1)),
  ),
  {
    name: "same",
    label: "is exactly",
    category: "comparison",
    typeCheck: (_ctx, args) => anyValue(args, 2),
    evaluate: (ctx) =>
      stableStringify(ctx.arg(0)) === stableStringify(ctx.arg(1)),
  },
  binaryComparison(
    "lt",
    "is less than",
    true,
    compare((a, b) => a < b),
  ),
  binaryComparison(
    "lte",
    "is less than or equal to",
    true,
    compare((a, b) => a <= b),
  ),
  binaryComparison(
    "gt",
    "is greater than",
    true,
    compare((a, b) => a > b),
  ),
  binaryComparison(
    "gte",
    "is greater than or equal to",
    true,
    compare((a, b) => a >= b),
  ),
  {
    name: "between",
    label: "is between",
    category: "comparison",
    typeCheck(ctx, args) {
      const err = arity(args, 3);
      if (err) {
        return err;
      }
      const types = [0, 1, 2].map((i) => valueArg(args, i));
      for (const t of types) {
        if (typeof t === "string") {
          return t;
        }
      }
      const [a, lo, hi] = types;
      if (
        typeof a === "string" ||
        typeof lo === "string" ||
        typeof hi === "string" ||
        !a ||
        !lo ||
        !hi
      ) {
        return "between expects three values";
      }
      if (
        !comparable(ctx.ts, a, lo, true) ||
        !comparable(ctx.ts, a, hi, true)
      ) {
        return "between bounds must match the value type";
      }
      return BOOLEAN;
    },
    evaluate(ctx) {
      const a = toComparable(ctx.arg(0));
      const lo = toComparable(ctx.arg(1));
      const hi = toComparable(ctx.arg(2));
      if (
        a === null ||
        lo === null ||
        hi === null ||
        typeof a === "boolean" ||
        typeof lo === "boolean" ||
        typeof hi === "boolean"
      ) {
        return false;
      }
      if (typeof a !== typeof lo || typeof a !== typeof hi) {
        return false;
      }
      return a >= lo && a <= hi;
    },
  },
  {
    name: "in",
    label: "is any of",
    category: "comparison",
    typeCheck(ctx, args) {
      const err = arity(args, 2);
      if (err) {
        return err;
      }
      const a = valueArg(args, 0);
      const list = valueArg(args, 1);
      if (typeof a === "string") {
        return a;
      }
      if (typeof list === "string") {
        return list;
      }
      const element = listElement(ctx, list);
      if (!element) {
        return "in expects a list of values";
      }
      if (!comparable(ctx.ts, a, element, false)) {
        return `cannot compare ${typeToString(a)} with ${typeToString(element)}`;
      }
      return BOOLEAN;
    },
    evaluate(ctx) {
      const a = ctx.arg(0);
      const list = ctx.arg(1);
      return Array.isArray(list) && list.some((item) => jsonEquals(a, item));
    },
  },
  {
    name: "includes",
    label: "includes",
    category: "list",
    typeCheck(ctx, args) {
      const err = arity(args, 2);
      if (err) {
        return err;
      }
      const list = valueArg(args, 0);
      const a = valueArg(args, 1);
      if (typeof a === "string") {
        return a;
      }
      if (typeof list === "string") {
        return list;
      }
      const element = listElement(ctx, list);
      if (!element) {
        return "includes expects a list";
      }
      if (!comparable(ctx.ts, element, a, false)) {
        return `cannot compare ${typeToString(element)} with ${typeToString(a)}`;
      }
      return BOOLEAN;
    },
    evaluate(ctx) {
      const list = ctx.arg(0);
      const a = ctx.arg(1);
      return Array.isArray(list) && list.some((item) => jsonEquals(a, item));
    },
  },
  textPredicate("contains", "contains", (a, b) => a.includes(b)),
  textPredicate("startsWith", "starts with", (a, b) => a.startsWith(b)),
  textPredicate("endsWith", "ends with", (a, b) => a.endsWith(b)),
  {
    name: "isSet",
    label: "exists",
    category: "existence",
    typeCheck: (_ctx, args) => anyValue(args, 1),
    evaluate: (ctx) => !isEmptyValue(ctx.arg(0)),
  },
  {
    name: "isEmpty",
    label: "is empty",
    category: "existence",
    typeCheck: (_ctx, args) => anyValue(args, 1),
    evaluate: (ctx) => isEmptyValue(ctx.arg(0)),
  },
  listLambda(
    "any",
    "at least one item matches",
    (items, fn) => items.some((item) => truthy(fn(item))),
    booleanBody,
  ),
  listLambda(
    "all",
    "every item matches",
    (items, fn) => items.every((item) => truthy(fn(item))),
    booleanBody,
  ),
  listLambda(
    "none",
    "no item matches",
    (items, fn) => !items.some((item) => truthy(fn(item))),
    booleanBody,
  ),
  listLambda(
    "countWhere",
    "number of items matching",
    (items, fn) => items.filter((item) => truthy(fn(item))).length,
    (body, element, ctx) =>
      booleanBody(body, element, ctx) === BOOLEAN
        ? nonNull("Int")
        : "predicate must return a boolean",
  ),
  listLambda(
    "sumOf",
    "sum of",
    (items, fn) =>
      items.reduce<number>((total, item) => {
        const value = fn(item);
        return total + (typeof value === "number" ? value : 0);
      }, 0),
    (body, _element, ctx) => {
      const base = body.kind === "named" ? baseOf(ctx.ts, body) : "list";
      return base === "number" || base === "json"
        ? NUMBER
        : "sumOf needs a number for each item";
    },
  ),
  listLambda(
    "filter",
    "items matching",
    (items, fn) => items.filter((item) => truthy(fn(item))),
    (body, element, ctx) =>
      booleanBody(body, element, ctx) === BOOLEAN
        ? listOf(element, false)
        : "predicate must return a boolean",
  ),
  {
    name: "count",
    label: "number of items",
    category: "list",
    typeCheck(ctx, args) {
      const err = arity(args, 1);
      if (err) {
        return err;
      }
      const list = valueArg(args, 0);
      if (typeof list === "string") {
        return list;
      }
      return listElement(ctx, list) ? nonNull("Int") : "count expects a list";
    },
    evaluate(ctx) {
      const list = ctx.arg(0);
      return Array.isArray(list) ? list.length : 0;
    },
  },
  {
    name: "length",
    label: "length",
    category: "text",
    typeCheck(ctx, args) {
      const err = arity(args, 1);
      if (err) {
        return err;
      }
      const t = valueArg(args, 0);
      if (typeof t === "string") {
        return t;
      }
      return baseOf(ctx.ts, t) === "string"
        ? nonNull("Int")
        : "length expects text";
    },
    evaluate(ctx) {
      const value = ctx.arg(0);
      return typeof value === "string" ? value.length : 0;
    },
  },
  {
    name: "add",
    label: "plus",
    category: "number",
    typeCheck(ctx, args) {
      const err = arity(args, 2) ?? numberArgs(ctx, args);
      if (err) {
        return err;
      }
      const types = valueArgs(args);
      if (typeof types === "string") {
        return types;
      }
      // Null unless both are numbers.
      const nullable = types.some(
        (t) => t.nullable || baseOf(ctx.ts, t) === "json",
      );
      return { ...NUMBER, nullable };
    },
    evaluate(ctx) {
      const a = ctx.arg(0);
      const b = ctx.arg(1);
      return typeof a === "number" && typeof b === "number" ? a + b : null;
    },
  },
  {
    name: "coalesce",
    label: "first value that is set",
    category: "existence",
    typeCheck(ctx, args) {
      if (args.length === 0) {
        return "coalesce expects at least one argument";
      }
      const types = valueArgs(args);
      if (typeof types === "string") {
        return types;
      }
      return unify(
        ctx.ts,
        types.map((type, i) => {
          const literal = literalOf(args, i);
          return literal ? { type, literal } : { type };
        }),
      );
    },
    evaluate(ctx) {
      for (let i = 0; i < ctx.count; i += 1) {
        const value = ctx.arg(i);
        if (value !== null) {
          return value;
        }
      }
      return null;
    },
  },
  {
    name: "isNull",
    label: "is missing",
    category: "existence",
    typeCheck: (_ctx, args) => anyValue(args, 1),
    evaluate: (ctx) => ctx.arg(0) === null,
  },
  {
    name: "toBoolean",
    label: "is truthy",
    category: "logic",
    typeCheck: (_ctx, args) => anyValue(args, 1),
    evaluate(ctx) {
      const value = ctx.arg(0);
      return value !== null && value !== false && value !== 0 && value !== "";
    },
  },
  {
    name: "toText",
    label: "as text",
    category: "text",
    typeCheck: (_ctx, args) =>
      arity(args, 1) ??
      (typeof valueArg(args, 0) === "string"
        ? "toText expects a value"
        : nonNull("String")),
    evaluate: (ctx) => jsText(ctx.arg(0)),
  },
  textToText("lower", "lowercase", (value) => value.toLowerCase()),
  {
    name: "containsExact",
    label: "contains (case-sensitive)",
    category: "text",
    typeCheck: (_ctx, args) => anyValue(args, 2),
    evaluate(ctx) {
      const a = ctx.arg(0);
      const b = ctx.arg(1);
      return typeof a === "string" && typeof b === "string" && a.includes(b);
    },
  },
  {
    name: "ageInYears",
    label: "age in years",
    category: "datetime",
    typeCheck(ctx, args) {
      const err = arity(args, 2);
      if (err) {
        return err;
      }
      const dates = dateArgs(ctx, args, [0, 1]);
      return typeof dates === "string" ? dates : named("Int");
    },
    evaluate(ctx) {
      const birth = ctx.arg(0);
      const at = ctx.arg(1);
      if (typeof birth !== "string" || typeof at !== "string") {
        return null;
      }
      const b = new Date(birth);
      const n = new Date(at);
      if (Number.isNaN(b.getTime()) || Number.isNaN(n.getTime())) {
        return null;
      }
      let years = n.getUTCFullYear() - b.getUTCFullYear();
      const beforeBirthday =
        n.getUTCMonth() < b.getUTCMonth() ||
        (n.getUTCMonth() === b.getUTCMonth() &&
          n.getUTCDate() < b.getUTCDate());
      if (beforeBirthday) {
        years -= 1;
      }
      return years;
    },
  },
  textToText("trim", "trimmed", (value) => value.trim()),
  {
    name: "toNumber",
    label: "as a number",
    category: "text",
    // Money and counts often arrive as text ("$1,250.00", "3 items"); null when there's no number in it.
    typeCheck(_ctx, args) {
      const err = arity(args, 1);
      if (err) {
        return err;
      }
      const t = valueArg(args, 0);
      return typeof t === "string" ? t : named("Float");
    },
    evaluate(ctx) {
      const value = ctx.arg(0);
      if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
      }
      if (typeof value !== "string") {
        return null;
      }
      const match = /-?\d[\d,]*(?:\.\d+)?|-?\.\d+/.exec(value);
      if (!match) {
        return null;
      }
      const parsed = Number(match[0].replace(/,/g, ""));
      return Number.isFinite(parsed) ? parsed : null;
    },
  },
  arithmetic("sub", "minus", (a, b) => a - b),
  arithmetic("mul", "times", (a, b) => a * b),
  arithmetic("div", "divided by", (a, b) => (b === 0 ? null : a / b)),
  {
    name: "now",
    label: "now",
    category: "datetime",
    typeCheck: (_ctx, args) => arity(args, 0) ?? DATETIME,
    evaluate: (ctx) => ctx.now,
  },
  {
    name: "dateAdd",
    label: "offset date",
    category: "datetime",
    typeCheck(ctx, args) {
      const err = arity(args, 3);
      if (err) {
        return err;
      }
      const d = valueArg(args, 0);
      const n = valueArg(args, 1);
      if (typeof d === "string") {
        return d;
      }
      if (typeof n === "string") {
        return n;
      }
      if (!isDateBase(baseOf(ctx.ts, d))) {
        return "dateAdd expects a date";
      }
      if (baseOf(ctx.ts, n) !== "number") {
        return "dateAdd amount must be a number";
      }
      const unit = choiceArg(ctx, args, 2, "the unit", TIME_UNITS);
      if (typeof unit === "string") {
        return unit;
      }
      return { ...DATETIME, nullable: d.nullable || n.nullable || !unit.known };
    },
    evaluate(ctx) {
      const d = ctx.arg(0);
      const n = ctx.arg(1);
      const u = ctx.arg(2);
      if (typeof d !== "string" || typeof n !== "number" || !isTimeUnit(u)) {
        return null;
      }
      return addTime(d, n, u);
    },
  },
  {
    name: "dateDiff",
    label: "time between",
    category: "datetime",
    typeCheck(ctx, args) {
      const err = arity(args, 3);
      if (err) {
        return err;
      }
      const dates = dateArgs(ctx, args, [0, 1]);
      if (typeof dates === "string") {
        return dates;
      }
      const unit = choiceArg(ctx, args, 2, "the unit", TIME_UNITS);
      if (typeof unit === "string") {
        return unit;
      }
      return {
        ...nonNull("Int"),
        nullable: dates.some((t) => t.nullable) || !unit.known,
      };
    },
    evaluate(ctx) {
      const a = ctx.arg(0);
      const b = ctx.arg(1);
      const u = ctx.arg(2);
      if (typeof a !== "string" || typeof b !== "string" || !isTimeUnit(u)) {
        return null;
      }
      const from = parseInstant(a);
      const to = parseInstant(b);
      if (from === null || to === null) {
        return null;
      }
      return Math.floor((to - from) / UNIT_MS[u]);
    },
  },
  {
    name: "datePart",
    label: "date part",
    category: "datetime",
    typeCheck(ctx, args) {
      const err = arity(args, 2);
      if (err) {
        return err;
      }
      const t = valueArg(args, 0);
      if (typeof t === "string") {
        return t;
      }
      if (!isDateBase(baseOf(ctx.ts, t))) {
        return "datePart expects a date";
      }
      const part = choiceArg(ctx, args, 1, "the part", DATE_PARTS);
      if (typeof part === "string") {
        return part;
      }
      return { ...nonNull("Int"), nullable: t.nullable || !part.known };
    },
    evaluate(ctx) {
      const d = ctx.arg(0);
      const part = ctx.arg(1);
      if (typeof d !== "string") {
        return null;
      }
      const ms = parseInstant(d);
      if (ms === null) {
        return null;
      }
      if (typeof part !== "string") {
        return null;
      }
      const date = new Date(ms);
      switch (part) {
        case "year":
          return date.getUTCFullYear();
        case "month":
          return date.getUTCMonth() + 1;
        case "day":
          return date.getUTCDate();
        case "dayOfWeek":
          return date.getUTCDay();
        case "hour":
          return date.getUTCHours();
        default:
          return null;
      }
    },
  },
];

export const FUNCTIONS: ReadonlyMap<string, FunctionDef> = new Map(
  definitions.map((d) => [d.name, d]),
);

export function getFunction(name: string): FunctionDef | undefined {
  return FUNCTIONS.get(name);
}

export function listTypeOf(element: TypeRef): TypeRef {
  return listOf(element, false);
}

export function isOrderedBase(
  base: ScalarBase | "enum" | "object" | "list",
): boolean {
  return ORDERED_BASES.includes(base);
}
